import { Controller } from '@hotwired/stimulus';

/**
 * Meetings Manager Motion Elections Controller
 * @deprecated This controller is being replaced by Turbo Streams for real-time updates
 * #AI: Legacy polling mechanism - kept for graceful degradation if Turbo Streams fail
 * #AI: Can be removed once Turbo Streams are fully validated in production
 */
export default class extends Controller {
  static targets = ['container'];
  static values = {
    organizationKey: String,
  };

  declare readonly containerTarget: HTMLElement;
  declare readonly organizationKeyValue: string;

  private pollingInterval: number | null = null;
  private readonly POLLING_INTERVAL_MS = 10000; // 10 seconds
  private readonly POLLING_ENABLED = false; // #AI: Disabled in favor of Turbo Streams

  /**
   * Initialize polling when component connects
   * #AI: Polling is now disabled - Turbo Streams handle updates
   */
  connect(): void {
    if (!this.POLLING_ENABLED) {
      console.info('[MeetingsManager] Polling disabled - using Turbo Streams for real-time updates');
      return;
    }

    // Don't start polling if we're in preview mode or input mode
    if (this.shouldPoll()) {
      this.startPolling();
    }
  }

  /**
   * Clean up polling interval when component disconnects
   */
  disconnect(): void {
    this.stopPolling();
  }

  /**
   * Determine if polling should be active
   */
  private shouldPoll(): boolean {
    // Don't poll if organization key is missing (preview or input mode)
    return !!this.organizationKeyValue && this.organizationKeyValue !== '';
  }

  /**
   * Start the polling interval
   */
  private startPolling(): void {
    this.pollingInterval = window.setInterval(() => {
      this.fetchElections();
    }, this.POLLING_INTERVAL_MS);
  }

  /**
   * Stop the polling interval
   */
  private stopPolling(): void {
    if (this.pollingInterval !== null) {
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Fetch updated elections from the server
   */
  private async fetchElections(): Promise<void> {
    try {
      const url = `/m/${this.organizationKeyValue}`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch elections:', response.statusText);
        return;
      }

      const html = await response.text();
      this.updateContainer(html);
    } catch (error) {
      console.error('Error fetching elections:', error);
    }
  }

  /**
   * Update the container with new HTML
   */
  private updateContainer(html: string): void {
    this.containerTarget.innerHTML = html;
  }
}
